import { supabase } from '../supabaseClient';

/**
 * Fetch all groups from the database
 * @returns {Promise<Array>} Array of all groups with id and name
 * @throws {Error} If database query fails
 */
export const fetchAllGroups = async () => {
  const { data, error } = await supabase
    .from('Group')
    .select('*')
    .order('name', { ascending: true });

  if (error) {
    throw new Error(error.message);
  }

  return data || [];
};

/**
 * Fetch all groups with their members
 * @returns {Promise<Array>} Array of groups with nested member data
 * @throws {Error} If database query fails
 */
export const fetchGroupsWithMembers = async () => {
  const { data, error } = await supabase
    .from('Group')
    .select('*, GroupMember(user:User(*))')
    .order('name', { ascending: true });

  if (error) {
    throw new Error(error.message);
  }

  return data || [];
};

/**
 * Update user's group memberships (replace all existing groups)
 * @param {string} userId - User ID
 * @param {Array<string>} groupIds - Array of group IDs
 * @returns {Promise<void>}
 * @throws {Error} If userId is missing, groupIds is not an array, or operation fails
 */
export const updateUserGroups = async (userId, groupIds) => {
  if (!userId) {
    throw new Error('User ID is required');
  }

  if (!Array.isArray(groupIds)) {
    throw new Error('Group IDs must be an array');
  }

  // First, delete all existing group memberships for this user
  const { error: deleteError } = await supabase
    .from('GroupMember')
    .delete()
    .eq('user', userId);

  if (deleteError) {
    throw new Error(deleteError.message);
  }

  // If groupIds is empty, we're done (user removed from all groups)
  if (groupIds.length === 0) {
    return;
  }

  // Insert new group memberships
  const memberships = groupIds.map(groupId => ({
    user: userId,
    group: groupId,
  }));

  const { error: insertError } = await supabase
    .from('GroupMember')
    .insert(memberships);

  if (insertError) {
    throw new Error(insertError.message);
  }
};
